export default {
    manage: {
        title: 'Manage Users',
        description: 'Invite people to your channel, choose their access, and see who has joined.',
        invite: 'Invite users',
        quickStart: {
            eyebrow: 'A QUICK START',
            titleFirst: 'Your team.',
            titleSecond: 'In one place.',
            description: 'Keep track of everyone in your channel and the access they have.',
            howItWorks: 'How it works',
            dismiss: 'Dismiss manage users guide',
            sectionsLabel: 'How to manage your channel’s users',
            footnote: 'Changes here apply to your current channel. Use search to find a person, then open their actions to manage them.',
            sections: {
                invite: {
                    title: 'Invite people',
                    description: 'Send an email invitation or share a personal invite link.',
                    example: 'Example: add a teammate or an external contributor.',
                },
                access: {
                    title: 'Choose their access',
                    description: 'Choose a role when you invite someone. Use the Role column to change their access later.',
                    example: 'Example: give a teammate Member access.',
                },
                track: {
                    title: 'See who has joined',
                    description: 'Use Active, Invited, Deleted, and Participants to find the people you need.',
                    example: 'Example: check Invited for people who haven’t joined.',
                },
            },
        },
    },
    inviteLinks: {
        title: 'Invite links',
        description: 'Bring people into {channel} with a personal invitation link.',
        channelFallback: 'your channel',
        copySuccess: 'Invite link copied',
        copySuccessDescription: 'Send it directly to the person you’re inviting.',
        copyError: 'Could not copy the link',
        copyErrorDescription: 'Select and copy it manually.',
        bulk: {
            emailLabel: 'User email', upload: 'Upload CSV',
            emailHelp: 'Add addresses separated by spaces or commas.',
            sampleCsv: 'Download a sample CSV', emailPlaceholder: "name{'@'}company.com", add: 'Add email',
            invalidEmail: 'Enter a valid email address.', duplicateEmail: 'One or more emails have already been added.',
            recipients: 'Invite recipients', accessFor: 'Access level for {email}', remove: 'Remove {email}',
            resend: 'Resend pending invites', cancel: 'Cancel', sending: 'Sending…', invite: 'Invite users',
        },
        modal: {
            title: 'Invite people to {channel}', description: 'Choose who to invite and the access they need.',
            learnMore: 'Learn about access levels', close: 'Close invitation dialog',
            optionTitle: 'Invite with a personal link', optionDescription: 'Create a link you can copy and share directly.', openLinks: 'Open invite links',
            audience: {
                legend: 'Invitation type',
                otherTitle: 'Standard & other roles', otherDescription: 'Choose their access below',
                socialTitle: 'Social user', socialDescription: 'Learn and share existing content',
            },
            social: {
                accessLabel: 'Social user access',
                allowedTitle: 'Learn and share', allowedFeeds: 'Company and Coaching feeds', allowedShare: 'Share existing content',
                restrictedTitle: 'No creation or requests',
                restrictedDescription: 'No recording, uploads, or sending or receiving requests. No My Media or AI Visibility.',
                emailLabel: 'Email address', emailPlaceholder: "name{'@'}company.com",
                staffError: 'MarketScale staff retain their MS Admin role.',
                hint: 'We’ll email them an invitation to join this channel.',
                existingMember: 'Already a member? Update their existing channel role.',
                cancel: 'Cancel', sending: 'Sending…', send: 'Send invite',
            },
        },
        roles: { admin: 'Admin', super: 'MS Admin', standard: 'Member', contributor: 'Contributor' },
        status: { pending: 'Pending', accepted: 'Accepted', expired: 'Expired', revoked: 'Revoked' },
        quickStart: {
            eyebrow: 'A QUICK START',
            titleFirst: 'Invite your team.',
            titleSecond: 'One link at a time.',
            description: 'Give each person a personal way to join your channel, with the access they need.',
            howItWorks: 'How it works',
            dismiss: 'Dismiss invite links guide',
            sectionsLabel: 'How to invite someone',
            footnote: 'Each link is personal. The recipient must join with the email you enter.',
            sections: {
                person: { title: 'Add the person', description: 'Enter their email. Add their name to personalize the invitation.', example: 'Example: invite a new teammate to your channel.' },
                access: { title: 'Choose their access', description: 'Choose an access level from the roles available to you, then generate their link.', example: 'Example: give a teammate Member access.' },
                share: { title: 'Share and track', description: 'Copy the link to share it directly. Check Sent invites to see who has joined or needs a reminder.', example: 'Example: resend a pending invite from the list.' },
            },
        },
        form: {
            title: 'Create an invite link',
            description: 'Add the person’s details and choose their access.',
            firstName: 'First name', lastName: 'Last name', optional: '(optional)',
            firstNamePlaceholder: 'First name', lastNamePlaceholder: 'Last name',
            email: 'Email', emailPlaceholder: "name{'@'}company.com",
            access: 'Access level', selectRole: 'Choose an access level',
            accessHelp: 'Choose what this person can access in your channel.',
            generating: 'Generating…', generate: 'Generate invite link',
            inviteAnother: 'Invite someone else',
            linkNote: 'Links work once and expire in 7 days. Generating a new link replaces the previous one.',
            ready: 'Invite link ready for {email}', linkLabel: 'Invite link', copied: 'Copied', copy: 'Copy link',
        },
        table: {
            title: 'Sent invites', description: 'See who has joined and manage invitations still waiting for a reply.',
            loading: 'Loading invites…', nameFallback: 'Name not provided',
            search: 'Search names or full email', filterLabel: 'Filter invites by status', allStatuses: 'All statuses',
            noMatchesTitle: 'No matching invites', noMatchesDescription: 'Try another name or full email address, or clear your filters.', clearFilters: 'Clear filters',
            expiredHint: 'Create a new link to invite again', range: '{from}–{to} of {total} invites', previous: 'Previous', next: 'Next',
            errorTitle: 'Could not load invites', errorDescription: 'Try again to see your channel’s invitations.', retry: 'Try again',
            emptyTitle: 'No invites yet', emptyDescription: 'Create your first invite to bring someone into this channel. Their status will appear here.',
            columns: { name: 'Name', email: 'Email', role: 'Access', status: 'Status', actions: 'Actions' },
            actions: {
                copy: 'Copy', copyLabel: 'Copy invite link', resend: 'Resend', resendLabel: 'Resend invite email',
                newLink: 'New link', newLinkLabel: 'Generate a new invite link', newLinkDescription: 'Create a fresh invitation. The previous link will no longer work.', revoke: 'Revoke', revokeLabel: 'Revoke invite', joined: 'Joined',
            },
        },
    },
    // A
    adminRoleDescription: 'Admins can create and delete channels, manage users, and have access to all software tools.',

    // C
    create: {},

    // E
    emailMagicLink: 'Email Magic link',
    enterPassword: 'Enter password',

    // I
    inviteColleagues: 'Invite colleagues',
    inviteWithEmail: 'Invite with email',
    inviteWithEmailText: 'Your colleagues will get an email that gives them access to your channel.',
    inviteYourColleagues: 'Invite Your Colleagues',
    inviteYourColleaguesText: 'Bring your team onboard and collaborate seamlessly. Your colleagues will receive an email granting them access to your channel.',
    inviteYourPartners: 'Invite Your External Community',
    inviteYourPartnersText: 'Expand your reach by inviting key partners to collaborate. Your invitees will receive a VIP Guest Pass, granting them exclusive access to the platform.',

    // M
    marketScaleAdminRoleDescription: 'MarketScale Admins have access to all software tools, can create and delete channels, and manage users.',

    // P
    pleaseSelectApprover: 'Please select approver',
    pleaseSelectColleague: 'Please select colleague',
    pleaseSelectExpert: 'Please select expert',
    profilePhotoSuccessfullyDeleted: 'Profile photo successfully deleted',

    // S
    standardRoleDescription: 'Standard users have access to all software tools to create and collaborate on media with colleagues and contributors.',

    // U
    updateUser: 'Update User',
    userInfo: 'User info',

    // V
    viewAsUser: 'View as {firstName} {lastName}',
    vipGuestRoleDescription: 'VIP Guests can create content, but cannot publish it.',
}
